<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="modelValue" class="fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm px-4" @click.self="handleClose">
        <div class="relative w-full max-w-xl rounded-3xl bg-white shadow-2xl ring-1 ring-slate-900/5">
          <button
            type="button"
            class="absolute right-4 top-4 rounded-full bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-hilab/40"
            @click="handleClose"
          >
            <X :size="18" />
          </button>

          <div class="px-8 py-10">
            <div class="mb-8 text-center">
              <span class="inline-flex items-center gap-2 rounded-full bg-hilab/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-hilab">Liên hệ tư vấn</span>
              <h2 class="mt-5 text-2xl font-semibold text-slate-900 md:text-3xl">Chúng tôi sẽ liên hệ lại trong thời gian sớm nhất</h2>
              <p class="mt-3 text-sm text-slate-500 md:text-base">
                Vui lòng điền thông tin bên dưới để đội ngũ Hi<span class="font-semibold">Chat</span>
                hiểu rõ nhu cầu và hỗ trợ bạn tốt hơn.
              </p>
            </div>

            <transition name="fade">
              <div v-if="submitted" class="flex flex-col items-center gap-4 rounded-2xl bg-hilab/10 p-6 text-center text-slate-700">
                <div class="flex h-14 w-14 items-center justify-center rounded-full bg-white text-hilab shadow-md">
                  <Check :size="24" />
                </div>
                <div>
                  <p class="text-lg font-semibold text-slate-900">Đã gửi yêu cầu thành công</p>
                  <p class="mt-1 text-sm text-slate-600">Đội ngũ của chúng tôi sẽ liên hệ qua số điện thoại hoặc email bạn cung cấp.</p>
                </div>
                <button type="button" class="primary-button mt-2 px-6 py-2 text-sm" @click="handleClose">Đóng</button>
              </div>
              <form v-else class="space-y-6" @submit.prevent="handleSubmit">
                <div class="grid gap-5 md:grid-cols-2">
                  <div class="md:col-span-2">
                    <label class="input-label" for="consultation-name">Tên người cần tư vấn <span class="text-hilab">*</span></label>
                    <input
                      id="consultation-name"
                      v-model.trim="form.name"
                      type="text"
                      required
                      autocomplete="name"
                      placeholder="Nguyễn Văn A"
                      class="input-field"
                      :class="{ 'input-field--error': errors.name }"
                    />
                    <p v-if="errors.name" class="input-error">
                      {{ errors.name }}
                    </p>
                  </div>

                  <div class="md:col-span-2">
                    <label class="input-label" for="consultation-phone">Số điện thoại <span class="text-hilab">*</span></label>
                    <PhoneInput
                      id="consultation-phone"
                      v-model="form.phone"
                      placeholder="Ví dụ: 0901234567"
                      :error="Boolean(errors.phone)"
                      @set-code="value => (form.country_code = value)"
                      @set-dial-code="value => (form.dial_code = value)"
                      @blur="() => validateField('phone')"
                    />
                    <p v-if="errors.phone" class="input-error">
                      {{ errors.phone }}
                    </p>
                  </div>

                  <div>
                    <label class="input-label" for="consultation-email">Email (tuỳ chọn)</label>
                    <input
                      id="consultation-email"
                      v-model.trim="form.email"
                      type="email"
                      autocomplete="email"
                      placeholder="you@example.com"
                      class="input-field"
                      :class="{ 'input-field--error': errors.email }"
                    />
                    <p v-if="errors.email" class="input-error">
                      {{ errors.email }}
                    </p>
                  </div>

                  <div>
                    <label class="input-label" for="consultation-company">Tên công ty (tuỳ chọn)</label>
                    <input id="consultation-company" v-model.trim="form.company" type="text" placeholder="Công ty TNHH ABC" class="input-field" />
                  </div>
                </div>

                <div>
                  <label class="input-label" for="consultation-notes">Nhu cầu tư vấn (tuỳ chọn)</label>
                  <textarea id="consultation-notes" v-model.trim="form.requirements" rows="3" placeholder="Chia sẻ thêm về nhu cầu hoặc câu hỏi của bạn..." class="input-field resize-none"></textarea>
                </div>

                <div v-if="errorMessage" class="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {{ errorMessage }}
                </div>

                <div class="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                  <button type="button" class="secondary-button w-full sm:w-auto" :disabled="submitting" @click="handleClose">Hủy</button>
                  <button type="submit" class="primary-button w-full sm:w-auto" :disabled="submitting">
                    <span v-if="!submitting">Gửi yêu cầu</span>
                    <span v-else class="flex items-center justify-center gap-2">
                      <span class="loader"></span>
                      Đang gửi...
                    </span>
                  </button>
                </div>
              </form>
            </transition>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { onBeforeUnmount, reactive, ref, watch } from 'vue';
import { X, Check } from 'lucide-vue-next';
import PhoneInput from '../../shared/components/PhoneInput.vue';
import publicAPI from '../api/public';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  source: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const form = reactive({
  name: '',
  phone: '',
  email: '',
  company: '',
  requirements: '',
  country_code: '',
  dial_code: '',
});

const errors = reactive({
  name: '',
  phone: '',
  email: '',
});

const submitting = ref(false);
const submitted = ref(false);
const errorMessage = ref('');

watch(
  () => props.modelValue,
  value => {
    if (value) {
      // eslint-disable-next-line no-use-before-define
      resetState();
      // eslint-disable-next-line no-use-before-define
      window.addEventListener('keydown', onKeydown);
    } else {
      // eslint-disable-next-line no-use-before-define
      window.removeEventListener('keydown', onKeydown);
    }
  }
);

const handleClose = () => {
  if (submitting.value) return;
  emit('update:modelValue', false);
};

const validateField = field => {
  switch (field) {
    case 'name':
      errors.name = form.name ? '' : 'Vui lòng nhập tên của bạn.';
      break;
    case 'phone':
      errors.phone = form.phone ? '' : 'Vui lòng nhập số điện thoại liên hệ.';
      break;
    case 'email':
      // eslint-disable-next-line no-use-before-define
      errors.email = form.email && !isValidEmail(form.email) ? 'Email không hợp lệ.' : '';
      break;
    default:
      break;
  }
};

const validateForm = () => {
  validateField('name');
  validateField('phone');
  validateField('email');
  return !errors.name && !errors.phone && !errors.email;
};

const handleSubmit = async () => {
  if (!validateForm()) return;

  submitting.value = true;
  errorMessage.value = '';

  try {
    // eslint-disable-next-line no-use-before-define
    const payload = buildPayload();
    await publicAPI.submitConsultation(payload);
    submitted.value = true;
  } catch (error) {
    const message = error?.response?.data?.errors?.join(', ') || error?.message || 'Đã xảy ra lỗi, vui lòng thử lại.';
    errorMessage.value = message;
  } finally {
    submitting.value = false;
  }
};

const buildPayload = () => {
  const metadata = {};
  const sourceKey = props.source || '';

  if (sourceKey) {
    metadata.source_key = sourceKey;
    if (sourceKey.startsWith('pricing:')) {
      const planName = sourceKey.split(':')[1];
      if (planName) {
        metadata.plan_name = planName;
      }
    }
  }

  const payload = {
    name: form.name,
    phone_number: form.phone,
    email: form.email,
    company: form.company,
    requirements: form.requirements,
    country_code: form.country_code,
    dial_code: form.dial_code,
    source: sourceKey || undefined,
    metadata: Object.keys(metadata).length ? metadata : undefined,
  };

  return Object.entries(payload).reduce((acc, [key, value]) => {
    if (value && (typeof value !== 'string' || value.trim().length > 0)) {
      acc[key] = typeof value === 'string' ? value.trim() : value;
    }
    return acc;
  }, {});
};

const resetState = () => {
  form.name = '';
  form.phone = '';
  form.email = '';
  form.company = '';
  form.requirements = '';
  form.country_code = '';
  form.dial_code = '';

  errors.name = '';
  errors.phone = '';
  errors.email = '';

  submitting.value = false;
  submitted.value = false;
  errorMessage.value = '';
};

const isValidEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const onKeydown = event => {
  if (event.key === 'Escape') {
    handleClose();
  }
};

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.input-label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: rgb(15 23 42);
}

.input-field {
  width: 100%;
  border-radius: 0.875rem;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background-color: rgba(248, 250, 252, 0.6);
  padding: 0.85rem 1rem;
  font-size: 0.95rem;
  color: rgb(15 23 42);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.input-field:focus {
  outline: none;
  border-color: rgba(189, 38, 75, 0.5);
  box-shadow: 0 0 0 4px rgba(189, 38, 75, 0.08);
  background-color: #fff;
}

.input-field--error {
  border-color: rgba(239, 68, 68, 0.8);
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.08);
  background-color: rgba(254, 242, 242, 0.8);
}

.input-error {
  margin-top: 0.4rem;
  font-size: 0.8rem;
  color: #ef4444;
}

.loader {
  height: 1rem;
  width: 1rem;
  border-radius: 9999px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
